// ── payroll.js — 한국 노동법 기반 급여 계산 ─────────────────
// 시급·근무시간 → 주휴수당·연장·야간·휴일·4대보험·근로소득세·3.3% 공제
// 매년 변경되는 요율은 PAYROLL_RATES 상수에서 관리.

// ─── 한국 노동법 상수 ─────────────────────────────────────────
export const PAYROLL_RATES = {
  // 4대보험 (2025년 기준, 매년 변경)
  nationalPension: 0.045,        // 국민연금 (회사 4.5% / 본인 4.5%)
  healthInsurance: 0.03545,      // 건강보험 (회사 3.545% / 본인 3.545%)
  longTermCare: 0.1295,          // 장기요양 (건보의 12.95%)
  employmentInsurance: 0.009,    // 고용보험 (본인 0.9%)
  // 산재보험은 사업주만 부담 (외식업 약 1.0%, 본인 공제 X)
  freelancer: 0.033,             // 알바·사업소득자 원천징수 3.3%
  dailyWithholding: 0.066,       // 일용직 원천징수 6.6% (소득세 6% + 지방세 0.6%)
};

// 일용직 비과세 한도 (소득세법 시행령 19조)
// 2024년 기준 일 187,000원. 2025년 개정으로 인상 가능 — 사용자 설정으로 override 가능
export const DAILY_TAX_FREE_LIMIT = 187000;

// 시간 기준 상수
export const PAYROLL_THRESHOLDS = {
  dailyOvertime: 8,              // 일 8h 초과 = 연장
  weeklyOvertime: 40,            // 주 40h 초과 = 연장
  weeklyHolidayMin: 15,          // 주 15h+ 만근 시 주휴수당 발생
  weeklyHolidayMax: 8,           // 주휴 1일치 = 최대 8h
};

// 기본 매장 설정 (사용자가 storeSettings로 override)
export const DEFAULT_PAYROLL_SETTINGS = {
  isOver5Employees: false,       // 5인 이상 사업장 (연장수당 1.5배 의무)
  nightStart: '22:00',           // 야간근무 시작
  nightEnd: '06:00',             // 야간근무 종료
  weeklyHolidayDay: 0,           // 주휴일 (0=일, 1=월 ... 6=토)
  payDay: 25,                    // 매월 급여 지급일
  weeklyHolidayEnabled: true,
};

// ─── 시간 유틸 ────────────────────────────────────────────────
export function timeToMinutes(t) {
  if (!t || typeof t !== 'string') return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function minutesToHours(m) {
  return Math.round((m / 60) * 100) / 100;
}

// 두 시간대(start1~end1)와 야간시간대(nightStart~nightEnd) 겹치는 분 수 계산
function overlapMinutes(s1, e1, s2, e2) {
  const start = Math.max(s1, s2);
  const end = Math.min(e1, e2);
  return Math.max(0, end - start);
}

// ─── 일별 근무 분석 ──────────────────────────────────────────
/**
 * 하루 근무 → 정규/연장/야간/휴일 시간 분배
 * blocks 배열 형식 지원 (분할 근무·다중 휴식). 기존 startTime/endTime/breakMinutes 도 호환.
 * @param {object} shift  { blocks?, startTime?, endTime?, breakMinutes?, isHoliday }
 * @param {object} settings  { nightStart, nightEnd }
 * @returns {object|null}
 */
export function analyzeShift(shift, settings = DEFAULT_PAYROLL_SETTINGS) {
  if (!shift) return null;
  if (shift.isOff) return null;

  // ── blocks 배열 형식 우선 ──
  let workBlocks = [];
  let breakBlocks = [];
  if (Array.isArray(shift.blocks) && shift.blocks.length > 0) {
    workBlocks = shift.blocks.filter(b => b.type === 'work');
    breakBlocks = shift.blocks.filter(b => b.type === 'break');
  } else if (shift.startTime && shift.endTime) {
    // 기존 단일 형식 호환
    workBlocks = [{ type: 'work', start: shift.startTime, end: shift.endTime }];
    const breakM = Number(shift.breakMinutes) || 0;
    if (breakM > 0) {
      const sM = timeToMinutes(shift.startTime);
      let eM = timeToMinutes(shift.endTime);
      if (eM <= sM) eM = 24 * 60;
      const mid = (sM + eM) / 2;
      const half = breakM / 2;
      breakBlocks = [{
        type: 'break',
        start: minutesToTimeStr(Math.max(sM, mid - half)),
        end:   minutesToTimeStr(Math.min(eM, mid + half)),
      }];
    }
  } else {
    return null;
  }

  // 근무 블록 합계 (분, 휴식 제외 전)
  let workMinutes = 0;
  for (const b of workBlocks) {
    let s = timeToMinutes(b.start);
    let e = timeToMinutes(b.end);
    if (e <= s) e += 24 * 60;
    workMinutes += Math.max(0, e - s);
  }
  // 휴식 블록 합계
  let breakMinutes = 0;
  for (const b of breakBlocks) {
    let s = timeToMinutes(b.start);
    let e = timeToMinutes(b.end);
    if (e <= s) e += 24 * 60;
    breakMinutes += Math.max(0, e - s);
  }
  const netMinutes = Math.max(0, workMinutes - breakMinutes);
  if (netMinutes <= 0) return null;

  const totalHours = minutesToHours(netMinutes);
  const regularHours = Math.min(totalHours, PAYROLL_THRESHOLDS.dailyOvertime);
  const overtimeHours = Math.max(0, totalHours - PAYROLL_THRESHOLDS.dailyOvertime);

  // ── 야간시간 분석 (각 work 블록 별로 22:00~06:00 겹침) ──
  const nightStart = timeToMinutes(settings.nightStart || '22:00');
  let nightEnd = timeToMinutes(settings.nightEnd || '06:00');
  if (nightEnd <= nightStart) nightEnd += 24 * 60;

  let nightOverlap = 0;
  for (const b of workBlocks) {
    let s = timeToMinutes(b.start);
    let e = timeToMinutes(b.end);
    if (e <= s) e += 24 * 60;
    // 당일·전날·다음날 야간 영역 모두 검사
    nightOverlap += overlapMinutes(s, e, nightStart, nightEnd);
    nightOverlap += overlapMinutes(s, e, nightStart - 24 * 60, nightEnd - 24 * 60);
    nightOverlap += overlapMinutes(s, e, nightStart + 24 * 60, nightEnd + 24 * 60);
  }
  // 휴식시간은 비례 차감
  if (workMinutes > 0) {
    nightOverlap = Math.max(0, nightOverlap - breakMinutes * (nightOverlap / workMinutes));
  }
  const nightHours = minutesToHours(nightOverlap);

  const holidayHours = shift.isHoliday ? totalHours : 0;

  return {
    date: shift.date,
    totalHours,
    regularHours: shift.isHoliday ? 0 : regularHours,
    overtimeHours: shift.isHoliday ? 0 : overtimeHours,
    nightHours,
    holidayHours,
    isHoliday: !!shift.isHoliday,
  };
}

// 분 → "HH:MM" 헬퍼
function minutesToTimeStr(m) {
  m = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// ─── 주별 그룹핑 (월요일 시작) ──────────────────────────────────
function groupByWeek(shifts) {
  const groups = {};
  for (const sh of shifts) {
    const date = new Date(sh.date);
    if (isNaN(date)) continue;
    const dayOfWeek = date.getDay();  // 0=일 ... 6=토
    const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() + offset);
    const key = weekStart.toISOString().slice(0, 10);
    if (!groups[key]) groups[key] = [];
    groups[key].push(sh);
  }
  return Object.values(groups);
}

// ─── 주휴수당 자격 검증 ───────────────────────────────────────
function calcWeeklyHoliday(weekAnalyses, settings) {
  if (!settings.weeklyHolidayEnabled) return 0;

  const totalHours = weekAnalyses.reduce((s, a) => s + a.totalHours, 0);
  if (totalHours < PAYROLL_THRESHOLDS.weeklyHolidayMin) return 0;

  // 만근 검증 — 모든 스케줄된 일자에 출근 (analyzeShift가 null 안 반환)
  // 빠진 날 검증은 호출 측에서 (이 함수는 실제 분석된 shift만 받음)

  const dailyAvg = weekAnalyses.length > 0 ? totalHours / weekAnalyses.length : 0;
  return Math.min(PAYROLL_THRESHOLDS.weeklyHolidayMax, Math.round(dailyAvg * 100) / 100);
}

// ─── 근로소득세 간이세액 (대략) ──────────────────────────────
// 정확한 lookup은 국세청 간이세액표 PDF를 참조해야 하지만, MVP는 단순 추정
function calcIncomeTax(monthlyPay, dependents = 1) {
  if (monthlyPay < 1_060_000) return 0;

  const taxableBase = Math.max(0, monthlyPay - 1_060_000);
  let rate = 0.05;
  if (monthlyPay > 3_000_000) rate = 0.10;
  if (monthlyPay > 5_000_000) rate = 0.15;
  if (monthlyPay > 7_500_000) rate = 0.18;

  // 부양가족 공제 (대략)
  const depAdjust = Math.max(0.5, 1 - (dependents - 1) * 0.05);
  return Math.max(0, Math.round(taxableBase * rate * depAdjust));
}

// ─── 직원별 월간 급여 계산 (메인 함수) ──────────────────────
/**
 * @param {object} employee  { id, name, type, hourlyWage, monthlyWage, fourInsurance: { enabled, dependents } }
 * @param {Array} monthShifts  [{ date, startTime, endTime, breakMinutes, isHoliday, isOff }]
 * @param {object} settings  매장 급여 설정
 * @returns {object} 급여 명세
 */
export function calcMonthlyPayroll(employee, monthShifts, settings = DEFAULT_PAYROLL_SETTINGS) {
  if (!employee) return null;

  // ── 일용직 분기 (type === 'daily') ──
  // 일급 × 근무일수, 4대보험 X, 일 18.7만원 비과세, 초과분 6.6% 원천징수
  if (employee.type === 'daily') {
    return calcDailyPayroll(employee, monthShifts, settings);
  }

  // 분석된 shift만
  const analyses = (monthShifts || [])
    .filter(s => !s.isOff)
    .map(s => analyzeShift(s, settings))
    .filter(Boolean);

  // 합계
  let regularHours = 0;
  let overtimeHours = 0;
  let nightHours = 0;
  let holidayHours = 0;
  let weeklyHolidayHours = 0;
  let totalHours = 0;

  for (const a of analyses) {
    regularHours += a.regularHours;
    overtimeHours += a.overtimeHours;
    nightHours += a.nightHours;
    holidayHours += a.holidayHours;
    totalHours += a.totalHours;
  }

  // 주휴수당
  if (settings.weeklyHolidayEnabled !== false) {
    const weeks = groupByWeek(analyses);
    for (const w of weeks) {
      weeklyHolidayHours += calcWeeklyHoliday(w, settings);
    }
  }

  // 급여 계산
  const wage = Number(employee.hourlyWage) || 0;
  const overtimeMultiplier = settings.isOver5Employees ? 1.5 : 1.0;
  const nightExtra = settings.isOver5Employees ? 0.5 : 0;        // 야간 가산 (5인+)
  const holidayMultiplier = settings.isOver5Employees ? 1.5 : 1.0;

  const basePay = Math.round(regularHours * wage);
  const overtimePay = Math.round(overtimeHours * wage * overtimeMultiplier);
  const nightPay = Math.round(nightHours * wage * nightExtra);   // 가산분만
  const holidayPay = Math.round(holidayHours * wage * holidayMultiplier);
  const weeklyHolidayPay = Math.round(weeklyHolidayHours * wage);

  // 월급제(monthlyWage)인 경우 시급 계산은 무시하고 monthlyWage 기준
  const isMonthlySalary = (employee.monthlyWage || 0) > 0;
  const totalEarnings = isMonthlySalary
    ? Number(employee.monthlyWage)
    : (basePay + overtimePay + nightPay + holidayPay + weeklyHolidayPay);

  // ── 공제 계산 ──
  const deductions = {};
  let totalDeduction = 0;

  // 단기(shortterm) 분기: 월 60시간 미만이면 4대보험 자동 면제 (산재만 사업주 부담)
  const isShortTerm = employee.type === 'shortterm';
  const under60h = isShortTerm && totalHours < 60;

  if (employee.type === 'freelancer' || (employee.type === 'parttime' && !employee.fourInsurance?.enabled)) {
    // 알바·사업소득자 → 3.3% 공제
    const tax = Math.round(totalEarnings * PAYROLL_RATES.freelancer);
    deductions.withholding3_3 = tax;
    totalDeduction = tax;
  } else if (under60h) {
    // 단기 월 60h 미만 → 4대보험 X, 3.3% 원천징수만
    const tax = Math.round(totalEarnings * PAYROLL_RATES.freelancer);
    deductions.withholding3_3 = tax;
    deductions._note = '월 60시간 미만 단기 — 4대보험 면제';
    totalDeduction = tax;
  } else if (employee.fourInsurance?.enabled) {
    // 정직원·4대보험 → 4대보험 + 근로소득세
    const np = Math.round(totalEarnings * PAYROLL_RATES.nationalPension);
    const hi = Math.round(totalEarnings * PAYROLL_RATES.healthInsurance);
    const ltc = Math.round(hi * PAYROLL_RATES.longTermCare);
    const ei = Math.round(totalEarnings * PAYROLL_RATES.employmentInsurance);
    const incomeTax = calcIncomeTax(totalEarnings, employee.fourInsurance?.dependents || 1);
    const localTax = Math.round(incomeTax * 0.1);
    deductions.nationalPension = np;
    deductions.healthInsurance = hi;
    deductions.longTermCare = ltc;
    deductions.employmentInsurance = ei;
    deductions.incomeTax = incomeTax;
    deductions.localTax = localTax;
    totalDeduction = np + hi + ltc + ei + incomeTax + localTax;
  }

  const netPay = Math.max(0, totalEarnings - totalDeduction);

  return {
    employeeId: employee.id,
    employeeName: employee.name,
    totalHours: Math.round(totalHours * 100) / 100,
    regularHours: Math.round(regularHours * 100) / 100,
    overtimeHours: Math.round(overtimeHours * 100) / 100,
    nightHours: Math.round(nightHours * 100) / 100,
    holidayHours: Math.round(holidayHours * 100) / 100,
    weeklyHolidayHours: Math.round(weeklyHolidayHours * 100) / 100,
    basePay,
    overtimePay,
    nightPay,
    holidayPay,
    weeklyHolidayPay,
    totalEarnings,
    deductions,
    totalDeduction,
    netPay,
    isMonthlySalary,
  };
}

// ─── 일용직 급여 계산 ──────────────────────────────────────────
// 일급 × 근무일수, 4대보험 X, 일 18.7만원 비과세, 초과분 6.6% 원천징수
//
// 일용직 정의 (소득세법): 3개월 미만 같은 고용주에게 일급으로 근로 제공하는 자
// 비과세 한도: 일 187,000원 (2024) → 일급이 이 한도 이하면 세금 0원
// 원천징수: (일급 - 187,000) × 6.6% (소득세 6% + 지방소득세 0.6%)
//
// @param {object} employee  { type: 'daily', dailyRate, name, ... }
// @param {Array} monthShifts  [{ date, isOff }] — isOff !== true인 날짜를 근무일로 카운트
function calcDailyPayroll(employee, monthShifts, settings = DEFAULT_PAYROLL_SETTINGS) {
  const dailyRate = Number(employee.dailyRate) || 0;
  const taxFreeLimit = Number(settings.dailyTaxFreeAmount) || DAILY_TAX_FREE_LIMIT;

  // 근무일수 (isOff 제외)
  const workDays = (monthShifts || []).filter(s => !s.isOff).length;

  // 총 지급액
  const totalEarnings = workDays * dailyRate;

  // 일별 원천징수 계산
  // 일급이 비과세 한도 초과하면 (일급 - 한도) × 6.6%
  const dailyTaxable = Math.max(0, dailyRate - taxFreeLimit);
  const dailyWithholding = Math.round(dailyTaxable * PAYROLL_RATES.dailyWithholding);
  const totalWithholding = dailyWithholding * workDays;

  // 일별 소득세 + 지방세 분리 (참고용)
  const dailyIncomeTax = Math.round(dailyTaxable * 0.06);
  const dailyLocalTax = dailyWithholding - dailyIncomeTax;

  const deductions = {};
  if (totalWithholding > 0) {
    deductions.withholdingDaily = totalWithholding;
    deductions.dailyIncomeTax = dailyIncomeTax * workDays;
    deductions.dailyLocalTax = dailyLocalTax * workDays;
    deductions._note = `일용직 6.6% 원천징수 (일 비과세 ${taxFreeLimit.toLocaleString()}원 초과분)`;
  } else {
    deductions._note = `일용직 — 일급 ${dailyRate.toLocaleString()}원 ≤ 비과세 한도 ${taxFreeLimit.toLocaleString()}원, 원천징수 0원`;
  }

  const netPay = totalEarnings - totalWithholding;

  return {
    employeeId: employee.id,
    employeeName: employee.name,
    employmentType: 'daily',
    workDays,
    dailyRate,
    totalHours: 0,                 // 일용직은 시간 단위 없음
    regularHours: 0,
    overtimeHours: 0,
    nightHours: 0,
    holidayHours: 0,
    weeklyHolidayHours: 0,
    basePay: totalEarnings,
    overtimePay: 0,
    nightPay: 0,
    holidayPay: 0,
    weeklyHolidayPay: 0,
    totalEarnings,
    deductions,
    totalDeduction: totalWithholding,
    netPay,
    isMonthlySalary: false,
    isDailyWorker: true,
    taxFreeLimit,
  };
}
